import { useEffect, useState } from 'react'
import { Box, Button, Flex, Heading, Input, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react'
import { UiBreadcrumb } from 'components/common'
import { useAppContext } from 'hooks/useAppContext'
import { getLegacyProjectImport, startLegacyProjectImport } from 'api'

const BreadcrumbNav = [
  {
    label: 'Legacy Import',
    isCurrentPage: true
  }
]

const emptyResult = {
  status: '',
  total: 0,
  processed: 0,
  created: 0,
  skipped: 0,
  located: 0,
  incomplete: 0,
  ungeocoded: [],
  incompleteRows: [],
  error: null
}

export const LegacyProjectImport = () => {
  const { createToast } = useAppContext()
  const [file, setFile] = useState(null)
  const [jobId, setJobId] = useState(() => window.sessionStorage.getItem('legacyImportJobId') || '')
  const [result, setResult] = useState(emptyResult)

  useEffect(() => {
    if (!jobId || result.status === 'done' || result.status === 'error') return undefined
    const timer = setInterval(async () => {
      try {
        const job = await getLegacyProjectImport(jobId)
        setResult(job)
        if (job.status === 'done' || job.status === 'error') {
          window.sessionStorage.removeItem('legacyImportJobId')
        }
      } catch (err) {
        window.sessionStorage.removeItem('legacyImportJobId')
        setResult(current => ({ ...current, status: 'error', error: 'The import status could not be loaded.' }))
      }
    }, 2000)
    return () => clearInterval(timer)
  }, [jobId, result.status])

  const handleUpload = async () => {
    if (!file) {
      createToast('error', 'Error', 'Choose a CSV file.')
      return
    }
    try {
      setResult({ ...emptyResult, status: 'running' })
      const job = await startLegacyProjectImport(file)
      window.sessionStorage.setItem('legacyImportJobId', job.id)
      setJobId(job.id)
      setResult(job)
    } catch (err) {
      const message = err.response?.data?.error || 'The import could not be started.'
      setResult({ ...emptyResult, status: 'error', error: message })
      createToast('error', 'Error', message)
    }
  }

  const running = result.status === 'running'

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">Legacy Import</Heading>
        </Flex>
        <Box borderWidth={1} borderColor="gray.200" p={6} mb={6}>
          <Text mb={4}>
            Upload the Closed Won CSV. A row is skipped only when its Zoho Opportunity ID is already on a project. Each imported project is marked legacy-zoho, stage Complete, with an install date of May 1, 2007.
          </Text>
          <Flex gap={3} alignItems="center">
            <Input
              type="file"
              accept=".csv,text/csv"
              p={1}
              onChange={(event) => setFile(event.target.files?.[0] || null)}
              disabled={running}
            />
            <Button colorScheme="blue" onClick={handleUpload} isLoading={running} flexShrink={0}>
              Import
            </Button>
          </Flex>
        </Box>

        {result.status && (
          <Box borderWidth={1} borderColor="gray.200" p={6}>
            <Text mb={4}>
              {result.status === 'running' && `Importing ${result.processed} of ${result.total || '…'}`}
              {result.status === 'done' && 'Import finished.'}
              {result.status === 'error' && (result.error || 'Import failed.')}
            </Text>
            <Flex gap={8} mb={6} wrap="wrap">
              <Text>Created: {result.created}</Text>
              <Text>Skipped: {result.skipped}</Text>
              <Text>Coordinates added: {result.located || 0}</Text>
              <Text>Incomplete: {result.incomplete}</Text>
              <Text>Missing map coordinates: {result.ungeocoded?.length || 0}</Text>
            </Flex>

            {result.ungeocoded?.length > 0 && (
              <Box mb={6}>
                <Text fontWeight="bold" mb={2}>Saved, but not on the map</Text>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Project</Th>
                      <Th>Opportunity ID</Th>
                      <Th>Company</Th>
                      <Th>Address</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {result.ungeocoded.map(row => (
                      <Tr key={`${row.id}-${row.zohoDealId}`}>
                        <Td>{row.id}</Td>
                        <Td>{row.zohoDealId}</Td>
                        <Td>{row.customerCompany}</Td>
                        <Td>{[row.customerAddress, row.customerCity, row.customerState, row.customerZip].filter(Boolean).join(', ')}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}

            {result.incompleteRows?.length > 0 && (
              <Box>
                <Text fontWeight="bold" mb={2}>Not imported</Text>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Opportunity ID</Th>
                      <Th>Company</Th>
                      <Th>Address</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {result.incompleteRows.map((row, index) => (
                      <Tr key={`${row.zohoDealId || 'row'}-${index}`}>
                        <Td>{row.zohoDealId}</Td>
                        <Td>{row.customerCompany}</Td>
                        <Td>{[row.customerAddress, row.customerCity, row.customerState, row.customerZip].filter(Boolean).join(', ')}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </>
  )
}
